import { FontStyles } from "../../utils/font-styles";
import { ValuationsStepConfig } from "./valuations-form";
import { UseMultiStepFormReturn } from "../../components/forms/use-multi-step-form";
import { ValuationsFormSchemaType } from "./valuations-form";
import { Button } from "../../components/buttons/button.tsx";
import { ReactNode } from "react";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-regular.css";
import { InlineTextButton } from "../../components/buttons/text-button/inline-text-button";

export interface ValuationsFormStepProps {
  stepConfig: ValuationsStepConfig;
  form: UseMultiStepFormReturn<ValuationsFormSchemaType, ValuationsStepConfig>;
  index: number;
  children?: ReactNode;
  isSubmitting?: boolean;
}
export interface ValuationsFormStepPropsWithHint extends ValuationsFormStepProps {
  text: string | string[];
  hideControls?: boolean;
  isSubmitting?: boolean;
}

export const ValuationsFormStep = (props: ValuationsFormStepPropsWithHint) => {
  const { index, form, stepConfig, text, hideControls = false, isSubmitting = false } = props;
  const { steps } = form;
  const active = form.currentStep === index;
  const completed = form.currentStep > index;
  const handleStepClick = async () => {
    if (active) return; // Don't do anything if already active

    if (index < form.currentStep) {
      // Moving back - always allowed
      form.goToStep(index);
    } else {
      // Moving forward - validate current step and advance one step
      const startingStep = form.currentStep;
      const isValid = await form.nextStep();
      if (isValid && startingStep + 1 < index) {
        // We advanced to the next step, but user wanted to go further
        // Check if the intermediate step is also valid
        const newStepFields = form.steps[startingStep + 1].fields;
        const intermediateStepValid = await form.form.trigger(newStepFields);
        if (intermediateStepValid) {
          // Intermediate step is valid, jump to target
          form.goToStep(index);
        }
        // Otherwise, stay on intermediate step with errors shown
      }
    }
  };

  return (
    <div
      data-step-index={index}
      className={`w-full p-6 gap-6.5 md:gap-7.5 flex flex-col ${index < steps.length - 1 ? "border-b border-solid border-foggy-firth" : ""}`}
    >
      <div
        className={`w-full flex gap-2.5 items-center ${completed ? "opacity-50" : ""} ${!active ? "cursor-pointer" : ""}`}
        onClick={!active ? () => void handleStepClick() : undefined}
      >
        <div className={"flex flex-col items-start gap-2 grow"}>
          <h3 className={`${FontStyles.h6} font-medium leading-none md:leading-none`}>{stepConfig.title}</h3>
          {text && form.currentStep >= index && (
            <div className={"flex flex-col gap-0.5"}>
              {Array.isArray(text) ? (
                text.map((paragraph, idx) => (
                  <p key={idx} className={`text-light-slate ${FontStyles["body-md"]} leading-snug md:leading-snug`}>
                    {paragraph}
                  </p>
                ))
              ) : (
                <p className={`text-light-slate ${FontStyles["body-md"]} leading-snug md:leading-snug`}>{text}</p>
              )}
            </div>
          )}
        </div>

        <div
          className={`rounded-full ${completed ? "bg-glen-50 text-glen" : "bg-loch-50 text-loch"} ${FontStyles["body-xl"]} leading-none md:leading-none flex justify-center items-center shrink-0 w-[40px] h-[40px] md:w-[44px] md:h-[44px]`}
        >
          {completed ? <i className="fa-sharp fa-regular fa-check" /> : index + 1}
        </div>
      </div>
      {active && props.children}
      {active && !hideControls && (
        <div className="flex flex-col md:flex-row md:justify-end gap-4 items-center">
          <Button
            variant="solid"
            colorVariant="secondary"
            label={form.isLastStep ? "Submit valuation request" : "Continue"}
            loadingLabel="Submitting valuation request..."
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={() => void form.nextStep()}
            className="w-full md:w-auto"
          />
          {index > 0 && (
            <InlineTextButton className="md:order-first" onClick={() => form.previousStep()}>
              Go back
            </InlineTextButton>
          )}
        </div>
      )}
    </div>
  );
};
